import { supabase } from "@/integrations/supabase/client";

export interface LinkedGoogleDriveAccount {
  userId: string;
  provider: "google_drive";
  googleSub: string;
  googleEmail: string | null;
  displayName: string | null;
  grantedScopes: string[];
  hasDriveScope: boolean;
  connectedAt: string;
  updatedAt: string;
  connectedEventCount: number;
}

export interface EventDriveStatus {
  eventId: string;
  connected: boolean;
  googleDriveUserId: string | null;
  folderId: string | null;
  folderName: string | null;
  connectedAt: string | null;
}

export interface DriveManageableEvent extends EventDriveStatus {
  slug: string;
  title: string;
}

type GoogleDriveFunctionResponse<T> = {
  ok: boolean;
  data?: T;
  error?: string;
};

const readFunctionErrorMessage = async (error: any): Promise<string> => {
  const response = error?.context;
  if (response && typeof response.clone === "function") {
    const cloned = response.clone();
    const contentType = cloned.headers?.get?.("content-type") ?? "";
    try {
      const body = contentType.includes("application/json") ? await cloned.json() : await cloned.text();
      if (typeof body === "object" && body && "error" in body) {
        return String((body as { error?: unknown }).error ?? error.message ?? "Google Drive request failed.");
      }
      if (typeof body === "string" && body.trim()) {
        return body;
      }
    } catch {
      // Fall through to generic error message below.
    }
  }

  return error?.message || "Google Drive request failed.";
};

const invokeGoogleDriveFunction = async <T>(body: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.functions.invoke("google-drive-link", { body });

  if (error) {
    throw new Error(await readFunctionErrorMessage(error));
  }

  const payload = data as GoogleDriveFunctionResponse<T> | null;
  if (!payload?.ok || payload.data === undefined) {
    throw new Error(payload?.error || "Google Drive request failed.");
  }

  return payload.data;
};

export interface SyncJobSummary {
  jobId: string;
  status: "running" | "completed" | "paused" | "failed" | "cancelled";
  synced: number;
  failed: number;
  total: number;
  createdAt: string;
  completedAt: string | null;
}

export interface SyncJobDetail extends SyncJobSummary {
  syncedPhotos: number;
  syncedVoices: number;
  failedPhotos: number;
  failedVoices: number;
  totalPhotos: number;
  totalVoices: number;
  errorCount: number;
}

export interface SyncBatchResult {
  status: string;
  synced: number;
  failed: number;
  total: number;
  batchSynced: number;
  batchFailed: number;
}

export interface GoogleDriveService {
  getCurrentLinkedDriveAccount(): Promise<LinkedGoogleDriveAccount | null>;
  beginDriveConnectFlow(input: { redirectPath: string }): Promise<{ authUrl: string }>;
  completeDriveConnectFlow(input: { code: string; state: string; origin: string }): Promise<{
    account: LinkedGoogleDriveAccount;
    redirectPath: string;
  }>;
  disconnectLinkedDriveAccount(): Promise<void>;
  connectEventToDrive(eventId: string): Promise<EventDriveStatus>;
  disconnectEventFromDrive(eventId: string): Promise<EventDriveStatus>;
  fetchEventDriveStatus(eventId: string): Promise<EventDriveStatus>;
  listManageableDriveEvents(): Promise<DriveManageableEvent[]>;
  uploadPhotoToDrive(input: {
    eventId: string;
    photoId: string;
    photoUrl: string;
    mimeType?: string;
  }): Promise<{ driveFileId: string }>;
  uploadVoiceToDrive(input: {
    eventId: string;
    voiceId: string;
    voiceUrl: string;
    mimeType?: string;
  }): Promise<{ driveFileId: string }>;

  // Backfill sync methods
  getUnsyncedCount(eventId: string, includeUnapproved?: boolean): Promise<{ photos: number; voices: number }>;
  startBackfillSync(eventId: string, options?: { includeUnapproved?: boolean; includeVoices?: boolean }): Promise<{ jobId: string | null; totalItems: number; message?: string }>;
  processSyncBatch(eventId: string, jobId: string): Promise<SyncBatchResult>;
  getSyncStatus(eventId: string, jobId: string): Promise<SyncJobDetail>;
  getLatestSyncJob(eventId: string): Promise<SyncJobSummary | null>;
  retryFailedSync(eventId: string, jobId: string): Promise<{ retriedCount: number; newlyFailed: number }>;
}

export const supabaseGoogleDriveService: GoogleDriveService = {
  async getCurrentLinkedDriveAccount() {
    return invokeGoogleDriveFunction<LinkedGoogleDriveAccount | null>({ action: "get_linked_account" });
  },

  async beginDriveConnectFlow({ redirectPath }) {
    return invokeGoogleDriveFunction<{ authUrl: string }>({
      action: "begin_connect",
      redirectPath,
      origin: window.location.origin,
    });
  },

  async completeDriveConnectFlow({ code, state, origin }) {
    return invokeGoogleDriveFunction<{
      account: LinkedGoogleDriveAccount;
      redirectPath: string;
    }>({
      action: "complete_connect",
      code,
      state,
      origin,
    });
  },

  async disconnectLinkedDriveAccount() {
    await invokeGoogleDriveFunction<{ success: true }>({ action: "disconnect_account" });
  },

  async connectEventToDrive(eventId) {
    return invokeGoogleDriveFunction<EventDriveStatus>({
      action: "connect_event",
      eventId,
    });
  },

  async disconnectEventFromDrive(eventId) {
    return invokeGoogleDriveFunction<EventDriveStatus>({
      action: "disconnect_event",
      eventId,
    });
  },

  async fetchEventDriveStatus(eventId) {
    return invokeGoogleDriveFunction<EventDriveStatus>({
      action: "get_event_status",
      eventId,
    });
  },

  async listManageableDriveEvents() {
    return invokeGoogleDriveFunction<DriveManageableEvent[]>({
      action: "list_manageable_events",
    });
  },

  async uploadPhotoToDrive({ eventId, photoId, photoUrl, mimeType = "image/jpeg" }) {
    return invokeGoogleDriveFunction<{ driveFileId: string }>({
      action: "upload_photo_to_drive",
      eventId,
      photoId,
      photoUrl,
      mimeType,
    });
  },

  async uploadVoiceToDrive({ eventId, voiceId, voiceUrl, mimeType = "audio/webm" }) {
    return invokeGoogleDriveFunction<{ driveFileId: string }>({
      action: "upload_voice_to_drive",
      eventId,
      voiceId,
      voiceUrl,
      mimeType,
    });
  },

  async getUnsyncedCount(eventId, includeUnapproved = false) {
    return invokeGoogleDriveFunction<{ photos: number; voices: number }>({
      action: "get_unsynced_count",
      eventId,
      includeUnapproved,
    });
  },

  async startBackfillSync(eventId, options = {}) {
    return invokeGoogleDriveFunction<{ jobId: string | null; totalItems: number; message?: string }>({
      action: "start_backfill_sync",
      eventId,
      includeUnapproved: options.includeUnapproved ?? false,
      includeVoices: options.includeVoices ?? true,
    });
  },

  async processSyncBatch(eventId, jobId) {
    return invokeGoogleDriveFunction<SyncBatchResult>({
      action: "process_sync_batch",
      eventId,
      jobId,
    });
  },

  async getSyncStatus(eventId, jobId) {
    return invokeGoogleDriveFunction<SyncJobDetail>({
      action: "get_sync_status",
      eventId,
      jobId,
    });
  },

  async getLatestSyncJob(eventId) {
    return invokeGoogleDriveFunction<SyncJobSummary | null>({
      action: "get_latest_sync_job",
      eventId,
    });
  },

  async retryFailedSync(eventId, jobId) {
    return invokeGoogleDriveFunction<{ retriedCount: number; newlyFailed: number }>({
      action: "retry_failed_sync",
      eventId,
      jobId,
    });
  },
};
